import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { Asset, SearchProductsQuery, SearchVariantsQuery } from '../../../common/generated-types';
import { Subscription } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { CartService } from '../../../core/providers/cart/cart.service';
import { StateService } from '../../../core/providers/state/state.service';
import { AddToCartResult, CollectionItemType, RoundedCornerSize } from '../../../common/enums';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { getRoundedClass } from '../../../common/utils/styles-helper';

@Component({
    selector: 'vsf-product-card-regular-type2',
    templateUrl: './product-card-regular-type2.component.html',
    styleUrls: ['./product-card-regular-type2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardRegularType2Component implements OnInit, AfterViewInit {
    @Input() item: SearchProductsQuery['search']['items'][number] | SearchVariantsQuery['search']['items'][number];
    @Input() displayType: CollectionItemType = CollectionItemType.Product;
    @Input() displaySubtitle: boolean = false;
    @Input() displayPrice: boolean = false;
    @Input() displayBuyButton: boolean = false;
    @Input() displayAlternativeImage: boolean = false;
    @Input() imgSize: 'sm-h' | 'sm-v' | 'md' | 'lg' = 'md';
    @Input() imgType: 'square' | 'rectangle' = 'rectangle';
    @Input() isMobile: boolean = false;
    @Input() productCardRoundedCorners: RoundedCornerSize = RoundedCornerSize.None;
    @Input() buttonText: string = 'Add to Cart';
    @Input() buttonColorMode: string = 'default';
    @Input() buttonBgColor: string = '';
    @Input() buttonTextColor: string = '';

    msrp: number | null = null;
    discountPercentage: number | null = null;
    inFlight = false;
    inStock = true;
    productBaseUrl = '';
    itemName: string;
    itemPrice: number | null = null;
    featuredAsset: Asset | null;
    hover: boolean = false;
    hoverCard: boolean = false;
    cardWidth = 0;
    cardHeight = 0;
    isCustomizable = false;
    variantId: string | undefined = undefined;
    private subscription = new Subscription();
    get currentUrl() { return this.router.url; }

    get linkCommands(): any[] {
        const base = (this.productBaseUrl || '/dp').split('/').filter(Boolean);
        return ['/', ...base, this.item?.slug].filter(Boolean);
    }

    constructor(
        private cartService: CartService,
        private stateService: StateService,
        private changeDetector: ChangeDetectorRef,
        private logger: NGXLogger,
        private route: ActivatedRoute,
        private router: Router,
        private elementRef: ElementRef,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {}

    ngOnInit() {
        if (this.item) {
            this.inStock = this.item.inStock;
            if(this.displayType === CollectionItemType.Product) {
                const product = this.item as SearchProductsQuery['search']['items'][number];
                this.itemName = product.productName;
                this.featuredAsset = product.productAsset as Asset;
                this.isCustomizable = product?.isProductCustomizable || product?.isProductVariantCustomizable || false;
            } else if(this.displayType === CollectionItemType.ProductVariant) {
                const variant = this.item as SearchVariantsQuery['search']['items'][number];
                this.itemName = variant.productVariantName;
                this.featuredAsset = variant.productVariantAsset as Asset || variant.productAsset as Asset;
                this.isCustomizable = variant.isProductVariantCustomizable || variant?.isProductCustomizable || false;
                this.variantId = variant.productVariantId;
            }
            this.updatePricingInfo();
            this.changeDetector.markForCheck();
        }
        this.stateService.select(state => state.storePath).subscribe(storePath => {
            this.productBaseUrl = `${storePath}/dp`;
        });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.calculateCardSize();
        });
    }

    get roundedClass(): string {
        return getRoundedClass(this.productCardRoundedCorners);
    }

    get resolvedButtonText(): string {
        const fallback = 'ADD TO CART';
        const txt = (this.buttonText ?? '').trim();
        return txt.length ? txt : fallback;
    }

    get isDisabled(): boolean {
        return this.inFlight || !this.inStock;
    }

    get defaultButtonClasses(): any {
        if (this.isDisabled) {
          return ['bg-zinc-600', 'text-white', 'border', 'border-[#2E3742]', 'cursor-not-allowed', 'opacity-90'];
        }
        if (this.buttonColorMode === 'custom') {
          return ['border', 'hover:opacity-90'];
        }
        return ['bg-white', 'text-[#2E3742]', 'border', 'border-[#2E3742]', 'hover:bg-primary-600', 'hover:text-white', 'hover:border-none', 'hover:font-bold'];
    }

    get buttonInlineStyle(): { [k: string]: string } | null {
        if (this.isDisabled || this.buttonColorMode !== 'custom') {
          return null;
        }
        const style: Record<string, string> = {};
        if (this.buttonBgColor) style['background-color'] = this.buttonBgColor;
        if (this.buttonTextColor) style['color'] = this.buttonTextColor;
        return style;
    }

    updatePricingInfo() {
        if(this.displayType === CollectionItemType.Product) {
            const product = this.item as SearchProductsQuery['search']['items'][number];
            if(product.price?.__typename === 'PriceRange' && product.price.min !== null && product.price.max !== null && product.price.min === product.price.max) {
                this.itemPrice = product.price.min;
            } else if(product.price?.__typename === 'SinglePrice' && product.price.value !== null) {
                this.itemPrice = product.price.value;
            }
            if(product.productVariantMSRP) {
                this.msrp = product.productVariantMSRP;
            }
        } else if(this.displayType === CollectionItemType.ProductVariant) {
            const variant = this.item as SearchVariantsQuery['search']['items'][number];
            if(variant.price?.__typename === 'PriceRange' && variant.price.min !== null && variant.price.max !== null && variant.price.min === variant.price.max) {
                this.itemPrice = variant.price.min;
            } else if(variant.price?.__typename === 'SinglePrice' && variant.price.value !== null) {
                this.itemPrice = variant.price.value;
            }
            if(variant.productVariantMSRP) {
                this.msrp = variant.productVariantMSRP;
            }
        }
        if (this.itemPrice && this.msrp && this.msrp > this.itemPrice) {
            this.discountPercentage = ((this.msrp - this.itemPrice) / this.msrp) * 100;
        }
    }

    onCardMouseEnter() {
        this.hoverCard = true;
        this.changeDetector.markForCheck();
    }
    onCardMouseLeave() {
        this.hoverCard = false;
        this.changeDetector.markForCheck();
    }

    onMouseEnter() {
        this.hover = true;
        if(this.displayType === CollectionItemType.Product) {
            const product = this.item as SearchProductsQuery['search']['items'][number];
            if (this.displayAlternativeImage && product?.productAlternativeAsset) {
                this.featuredAsset = product?.productAlternativeAsset as Asset;
            }
        } else if(this.displayType === CollectionItemType.ProductVariant) {
            const variant = this.item as SearchVariantsQuery['search']['items'][number];
            if (this.displayAlternativeImage && variant?.productVariantAlternativeAsset) {
                this.featuredAsset = variant?.productVariantAlternativeAsset as Asset || variant?.productAlternativeAsset as Asset;
            }
        }
        this.changeDetector.markForCheck();
    }

    onMouseLeave() {
        this.hover = false;
        if(this.displayType === CollectionItemType.Product) {
            const product = this.item as SearchProductsQuery['search']['items'][number];
            this.featuredAsset = product.productAsset as Asset;
        } else if(this.displayType === CollectionItemType.ProductVariant) {
            const variant = this.item as SearchVariantsQuery['search']['items'][number];
            this.featuredAsset = variant.productVariantAsset as Asset || variant.productAsset as Asset;
        }
        this.changeDetector.markForCheck();
    }

    onButtonClick(event: MouseEvent) {
        if(this.isCustomizable) {
            this.router.navigate(
                [this.productBaseUrl, this.item.slug],
                { queryParams: { variantid: this.variantId } }
              );
        } else {
            event.preventDefault();
            event.stopPropagation();
            this.addToCartAndOpenDrawer();
        }
    }

    addToCartAndOpenDrawer() {
        if (!this.item) {
            return;
        }
        this.inFlight = true;
        this.changeDetector.markForCheck();

        this.subscription.add(
          this.cartService.addToCartAndOpenDrawer(this.item.productVariantId).subscribe({
            next: (result: AddToCartResult) => {
                this.inFlight = false;
                if (result === AddToCartResult.OUT_OF_STOCK) {
                    this.inStock = false;
                }
                this.changeDetector.markForCheck();
            },
            error: err => {
              this.inFlight = false;
              this.logger.error('Error in addToCartAndOpenDrawer', err);
              this.changeDetector.markForCheck();
            },
          }),
        );
    }

    calculateCardSize() {
        if (!isPlatformBrowser(this.platformId)) {
            // initialize card size
            this.cardWidth = this.route.snapshot.routeConfig?.path === 'search' ? 243 : 223;
            this.cardHeight = this.cardWidth;
            return;
        }
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        if (this.route.snapshot.routeConfig?.path === 'search') {
            this.cardWidth = rect.width;
            this.cardHeight = rect.width;
        } else {
            this.cardWidth = rect.width - 20;
            this.cardHeight = rect.width - 20;
        }
        this.changeDetector.markForCheck();
    }
}
